import { useEffect, useState } from 'react';
import {
  CATEGORIES,
  DENY_ALL,
  GRANT_ALL,
  categoriesToSignals,
  readConsent,
  saveConsent,
  signalsToCategories,
  type ConsentChoice,
  type ConsentState,
} from '../consent/consentMode';
import { Button } from './ui';

export function ConsentModal() {
  const [open, setOpen] = useState(() => readConsent() === null);
  const [details, setDetails] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    signalsToCategories(readConsent()?.signals ?? DENY_ALL),
  );

  // Otwarcie z linku „Ustawienia prywatności" w stopce.
  useEffect(() => {
    const onOpen = () => {
      setSelected(signalsToCategories(readConsent()?.signals ?? DENY_ALL));
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener('nf:consent-open', onOpen);
    return () => window.removeEventListener('nf:consent-open', onOpen);
  }, []);

  // Dopóki użytkownik nie zdecydował, blokujemy przewijanie strony pod modalem.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const decide = (signals: ConsentState, choice: ConsentChoice) => {
    saveConsent(signals, choice);
    setOpen(false);
    setDetails(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 p-3 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-title"
    >
      {/*
        max-h + flex-col sprawiają, że na niskich ekranach przewija się treść,
        a stopka z przyciskami zostaje na miejscu. Bez tego przy rozwiniętych
        szczegółach na telefonie nie dałoby się kliknąć „Odrzuć".
      */}
      <div className="flex max-h-[90dvh] w-full max-w-2xl flex-col rounded-xl border border-[#26304d] bg-[#0f1526] shadow-2xl fade-in">
        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          <h2 id="consent-title" className="text-lg font-semibold text-white">
            Dbamy o Twoją prywatność
          </h2>
          <p className="mt-2 text-[13px] leading-relaxed text-slate-400">
            Używamy plików cookie i podobnych technologii, żeby sklep działał poprawnie oraz — za Twoją zgodą — aby
            analizować ruch i mierzyć skuteczność reklam. Możesz zaakceptować wszystkie, odrzucić opcjonalne albo wybrać
            samodzielnie. Decyzję zmienisz w każdej chwili przez link{' '}
            <span className="text-slate-300">Ustawienia prywatności</span> w stopce.
          </p>

          {details && (
            <div className="mt-4 space-y-2">
              {CATEGORIES.map((cat) => {
                const on = cat.required || selected[cat.id];
                return (
                  <div key={cat.id} className="rounded-lg border border-[#26304d] bg-[#0b1020] px-3.5 py-3">
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={Boolean(on)}
                        disabled={cat.required}
                        onChange={(e) => setSelected((s) => ({ ...s, [cat.id]: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#22d3a6] disabled:opacity-50"
                      />
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-[13.5px] font-medium text-slate-100">{cat.label}</span>
                          {cat.required && (
                            <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10.5px] text-slate-400">
                              zawsze aktywne
                            </span>
                          )}
                        </span>
                        <span className="mt-1 block text-[12px] leading-relaxed text-slate-400">
                          {cat.description}
                        </span>
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-t border-[#26304d] p-4 sm:flex-row sm:items-center sm:p-5">
          {/* Odrzucenie musi być tak samo dostępne jak akceptacja. */}
          <Button variant="ghost" className="sm:order-1" onClick={() => decide(DENY_ALL, 'reject_all')}>
            Odrzuć opcjonalne
          </Button>

          {details ? (
            <Button
              variant="soft"
              className="sm:order-2"
              onClick={() => decide(categoriesToSignals(selected), 'custom')}
            >
              Zapisz wybór
            </Button>
          ) : (
            <Button variant="ghost" className="sm:order-2" onClick={() => setDetails(true)}>
              Dostosuj
            </Button>
          )}

          <Button className="sm:order-3 sm:ml-auto" onClick={() => decide(GRANT_ALL, 'accept_all')}>
            Akceptuję wszystkie
          </Button>
        </div>
      </div>
    </div>
  );
}
